import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Skeleton,
  Tooltip,
  IconButton,
  Chip,
  Button,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import apiService from "../../../service/Api";
import {
  setCache,
  type ComponentManager,
  type Developer,
} from "./AssignmentsCache";
import figmaLogoPng from "../../../assets/figmalogo.png";
import { BugReport } from "@mui/icons-material";


// ─── Helpers ─────────────────────────────────────────────────────────────────

const deriveComponentName = (pathname: string) => {
  const segments = pathname.split("/").filter(Boolean);
  return segments.slice(0, 2).join("/");
};

const getDeveloperName = (dev?: Developer) => {
  if (!dev) return "Not Assigned";
  const full = [dev.first_name, dev.last_name].filter(Boolean).join(" ");
  return full || dev.username;
};

const normalise = (s: string) => s.toLowerCase().replace(/[\s\-_/]/g, "");

/** Client-side fallback when the backend doesn't return review_days. */
const calcDaysToReview = (auditDateStr?: string | null): number | null => {
  if (!auditDateStr) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const audit = new Date(auditDateStr);
  if (isNaN(audit.getTime())) return null;
  audit.setHours(0, 0, 0, 0);
  return Math.ceil((audit.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
};

/**
 * Returns the colour for the days-to-review chip.
 * Mirrors the logic in UpdateFooter so both components stay in sync.
 */
const daysChipColor = (
  days: number | null,
): "default" | "error" | "warning" | "info" | "success" => {
  if (days === null) return "default";
  if (days <= 0) return "error";
  if (days <= 7) return "warning";
  if (days <= 30) return "info";
  return "success";
};

const daysChipLabel = (days: number | null): string => {
  if (days === null) return "No audit";
  if (days < 0) return `${Math.abs(days)}dys overdue`;
  if (days === 0) return "Due today";
  return `${days} dys to audit`;
};

const fetchFresh = async (): Promise<ComponentManager[]> => {
  let all: ComponentManager[] = [];
  let url: string | null = "/api/configurations/components-managers/";

  while (url) {
    const response: {
      data:
        | { results?: ComponentManager[]; next?: string | null }
        | ComponentManager[];
    } = await apiService.get(url);

    if (Array.isArray(response.data)) {
      all = response.data;
      break;
    } else {
      const paginated = response.data as {
        results?: ComponentManager[];
        next?: string | null;
      };
      all = [...all, ...(paginated.results || [])];
      const next = paginated.next ?? null;
      if (next) {
        const urlObj = new URL(next);
        url = urlObj.pathname + urlObj.search;
      } else {
        url = null;
      }
    }
  }

  setCache(all);
  return all;
};

// ─── Component ────────────────────────────────────────────────────────────────

const Footer = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [managers, setManagers] = useState<ComponentManager[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data = await fetchFresh();
        setManagers(data);
      } catch (error) {
        console.error("Failed to fetch component managers:", error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    const handleUpdate = async () => {
      setLoading(true);
      try {
        const data = await fetchFresh();
        setManagers(data);
      } catch (error) {
        console.error("Failed to refresh component managers:", error);
      } finally {
        setLoading(false);
      }
    };

    window.addEventListener("assignments-updated", handleUpdate);
    return () =>
      window.removeEventListener("assignments-updated", handleUpdate);
  }, []);

  const componentName = deriveComponentName(location.pathname);
  const baseComponentName = location.pathname.split("/").filter(Boolean)[0];

  const match =
    managers.find(
      (m) => normalise(m.component.name) === normalise(componentName),
    ) ||
    managers.find(
      (m) => normalise(m.component.name) === normalise(baseComponentName),
    );

  const whatsappUrl = match?.whatsapp_group_url ?? null;
  const figmaUrl = match?.figma_url ?? null;

  // Pull new fields from the extended assignment object
  type ExtendedManager = ComponentManager & {
    user_lead?: Developer;
    next_audit?: string | null;
    review_days?: number | null;
  };
  const extMatch = match as ExtendedManager | undefined;
  const userLead = extMatch?.user_lead;

  const daysToReview: number | null =
    extMatch?.review_days != null
      ? extMatch.review_days
      : calcDaysToReview(extMatch?.next_audit);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 1,
          borderTop: "1px solid",
          borderColor: "divider",
          mt: 2,
          backgroundColor: "background.paper",
        }}
      >
        <Box sx={{ width: "100%" }}>
          <Skeleton width={160} height={20} />
          <Skeleton width={300} height={16} />
        </Box>
      </Box>
    );
  }

  // ── Event handlers ──────────────────────────────────────────────────────────

  const handleFooterClick = () => {
    navigate("/configurations/components-manager", {
      state: {
        componentName: match?.component.name ?? componentName,
        componentId: match?.component.id ?? null,
      },
    });
  };

  const handleWhatsAppClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.open(whatsappUrl!, "_blank", "noopener,noreferrer");
  };

  const handleFigmaClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.open(figmaUrl!, "_blank", "noopener,noreferrer");
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Tooltip
      title={
        match
          ? "Click to view or edit this component's assignment"
          : "Click to assign a lead to this component"
      }
      placement="top"
    >
      <Box
        onClick={handleFooterClick}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          px: 2,
          py: 1.25,
          borderTop: "1px solid",
          borderColor: "divider",
          mt: 2,
          backgroundColor: "background.paper",
          cursor: "pointer",
          transition: "background-color 0.2s",
          "&:hover": { backgroundColor: "action.hover" },
        }}
      >
        {/* ── Col 1: component name + dev team ──────────────────── */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Component name + quick-links */}
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.25 }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "0.875rem",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {match?.component.name ?? componentName}
            </Typography>

            {/* WhatsApp icon – always visible, disabled when no URL */}
            <Tooltip
              title={
                whatsappUrl ? "Open WhatsApp group" : "No WhatsApp group linked"
              }
              placement="top"
            >
              <span>
                <IconButton
                  onClick={whatsappUrl ? handleWhatsAppClick : undefined}
                  size="small"
                  disabled={!whatsappUrl}
                  sx={{
                    backgroundColor: whatsappUrl ? "#25D366" : "#bdbdbd6c",
                    color: whatsappUrl ? "#fff" : "#fff",

                    p: 0.4,
                    borderRadius: "4px",
                    "&:hover": whatsappUrl
                      ? {
                          backgroundColor: "#128C7E",
                          transform: "scale(1.1)",
                          transition: "all 0.2s",
                        }
                      : {},
                  }}
                >
                  <WhatsAppIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>

            {/* Figma icon – always visible, disabled when no URL */}
            <Tooltip
              title={figmaUrl ? "Open Figma design" : "No Figma design linked"}
              placement="top"
            >
              <span>
                <IconButton
                  onClick={figmaUrl ? handleFigmaClick : undefined}
                  size="small"
                  disabled={!figmaUrl}
                  sx={{
                    p: 0.4,
                    borderRadius: "4px",
                    backgroundColor: figmaUrl ? "transparent" : "#bdbdbd",

                    "&:hover": figmaUrl
                      ? {
                          backgroundColor: "transparent",
                          transform: "scale(1.15)",
                        }
                      : {},
                    transition: "all 0.2s",
                    opacity: figmaUrl ? 1 : undefined, // MUI disabled handles opacity
                  }}
                >
                  <Box
                    component="img"
                    src={figmaLogoPng}
                    alt="Figma"
                    sx={{
                      width: 26,
                      height: 26,
                      display: "block",
                      objectFit: "contain",

                      filter: figmaUrl ? undefined : "grayscale(1)",
                      opacity: figmaUrl ? 1 : 0.6,
                    }}
                  ></Box>
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          {/* Lead + Backup on their own line */}
          {match ? (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                fontSize: "12px",
              }}
            >
              <strong>Lead:</strong> {getDeveloperName(match.lead_developer)}
              {"  ·  "}
              <strong>Backup:</strong>{" "}
              {getDeveloperName(match.backup_developer)}
            </Typography>
          ) : (
            <Typography variant="caption" sx={{ color: "text.disabled" }}>
              Not assigned — click to assign a lead
            </Typography>
          )}
        </Box>

        {/*Report Bug Button*/}
        <Button
              variant="outlined"
              color="error"
              startIcon={<BugReport/>}
              onClick={(e) => {
                e.stopPropagation();
                window.dispatchEvent(new CustomEvent("open-bug-report"));
              }}
            >
              Report Bug
            </Button>
        {match && (
          <Box
            sx={{
              flexShrink: 0,
              borderLeft: "1px solid",
              borderColor: "divider",
              pl: 2,
              minWidth: 140,
            }}
          >
            <Typography variant="caption" sx={{ display: "block", mb: 0.25 }}>
              USER LEAD
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, fontSize: "12px" }}
            >
              {userLead ? getDeveloperName(userLead) : "—"}
            </Typography>
          </Box>
        )}

        {/* ── Col 3: version + audit chip ───────────────────────── */}
        {match && (
          <Box
            sx={{
              flexShrink: 0,
              borderLeft: "1px solid",
              borderColor: "divider",
              pl: 2,
              mr: 5,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 0.5,
              minWidth: 120,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {match.version && (
              <Typography variant="caption" color="text.disabled">
                v{match.version}
              </Typography>
            )}
            <Tooltip
              title={
                daysToReview === null
                  ? "No audit date set"
                  : daysToReview <= 0
                    ? "Audit is overdue!"
                    : `Next audit in ${daysToReview} day${daysToReview === 1 ? "" : "s"}`
              }
              placement="top"
            >
              <Chip
                icon={<AccessTimeIcon sx={{ fontSize: "14px !important" }} />}
                label={daysChipLabel(daysToReview)}
                color={daysChipColor(daysToReview)}
                size="small"
                variant={
                  daysToReview !== null && daysToReview <= 7
                    ? "filled"
                    : "outlined"
                }
                sx={{ cursor: "default" }}
              />
            </Tooltip>
          </Box>
        )}
      </Box>
    </Tooltip>
  );
};

export default Footer;
